import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-datatable-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss']
})
export class DatatablePaginationComponent implements OnChanges {
  @Input() currentPage: number = 1;      // 1-indexed page number
  @Input() totalItems: number = 0;       // Total record count
  @Input() pageSize: number = 10;        // Items per page
  @Output() pageChange = new EventEmitter<number>();

  totalPages: number = 0;
  visiblePages: (number | string)[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['totalItems'] || changes['pageSize'] || changes['currentPage']) {
      this.calculatePagination();
    }
  }

  private calculatePagination(): void {
    this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
    this.visiblePages = this.getVisiblePages();
  }

  /**
   * Calculate which page numbers to display with smart truncation
   * Shows max 7 page buttons with ellipsis for large page counts
   * 
   * Examples:
   * - Pages 1-7: [1] [2] [3] [4] [5] [6] [7]
   * - Current page 5 of 20: [1] ... [4] [5] [6] ... [20]
   * - Current page 2 of 20: [1] [2] [3] ... [20]
   * - Current page 19 of 20: [1] ... [18] [19] [20]
   */
  private getVisiblePages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    
    // Always show first, last, current, and adjacent pages
    const pages: Set<number> = new Set([1, total, current]);
    
    // Add pages around current
    if (current > 1) pages.add(current - 1);
    if (current < total) pages.add(current + 1);
    
    // Convert to sorted array and add ellipsis
    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    const result: (number | string)[] = [];
    
    for (let i = 0; i < sortedPages.length; i++) {
      if (i > 0 && sortedPages[i] - sortedPages[i - 1] > 1) {
        result.push('...');
      }
      result.push(sortedPages[i]);
    }
    
    return result;
  }

  /**
   * Navigate to a specific page
   * @param page Page number (1-indexed)
   */
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.pageChange.emit(page);
  }

  /**
   * Check if a page value is a number (not ellipsis)
   */
  isNumber(value: number | string): value is number {
    return typeof value === 'number';
  }
}
